import React from "react";
import {
  Shield,
  Lock,
  Cookie,
  UserCheck,
  Database,
  FileText,
  Mail,
} from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Banner */}
      <section className="bg-primary text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg md:text-xl opacity-90">
            Your privacy is important to us. Learn how we protect your personal
            information.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 mb-8">
              At <strong>Assess Trademark</strong> we are committed to
              protecting your privacy and ensuring that your personal
              information is handled in a safe and responsible manner. This
              Privacy Policy outlines how we collect, use, and protect your
              personal data, and it applies to all interactions with our
              website, products, and services.
            </p>

            {/* Information We Collect */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Database className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Information We Collect
                </h2>
              </div>
              <p className="text-gray-700 mb-4">
                We collect personal information from you when you visit our
                website, register for an account, make a purchase, or
                communicate with us. The types of information we may collect
                include:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li>
                  <strong>Personal Identification Information:</strong> Name,
                  email address, phone number, billing and shipping addresses,
                  and payment information.
                </li>
                <li>
                  <strong>Non-personal Identification Information:</strong> IP
                  address, browser type, operating system, and website usage
                  information.
                </li>
                <li>
                  <strong>Cookies and Tracking Technologies:</strong> We use
                  cookies and similar tracking technologies to enhance your
                  browsing experience, analyze website traffic, and personalize
                  content.
                </li>
              </ul>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-secondary/20 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-secondary" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  How We Use Your Information
                </h2>
              </div>
              <p className="text-gray-700 mb-4">
                We use the information we collect for various purposes,
                including:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li>To process and manage your transactions and orders.</li>
                <li>
                  To communicate with you about your orders, services, or
                  updates.
                </li>
                <li>
                  To respond to your inquiries and provide customer support.
                </li>
                <li>To improve our website, products, and services.</li>
                <li>
                  To send promotional materials, newsletters, and other
                  marketing communications (if you have opted-in to receive
                  them).
                </li>
                <li>
                  To prevent fraud and ensure the security of our website.
                </li>
              </ul>
            </div>

            {/* How We Protect Your Information */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Lock className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  How We Protect Your Information
                </h2>
              </div>
              <p className="text-gray-700 mb-4">
                We implement a variety of security measures to maintain the
                safety of your personal information, including:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li>
                  <strong>Encryption:</strong> We use SSL encryption technology
                  to protect sensitive data during transmission.
                </li>
                <li>
                  <strong>Secure Storage:</strong> Your personal information is
                  stored in a secure environment with restricted access.
                </li>
                <li>
                  <strong>Regular Audits:</strong> We regularly review our
                  security practices to ensure that your data is safeguarded.
                </li>
              </ul>
            </div>

            {/* Sharing Your Information */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Sharing Your Information
                </h2>
              </div>
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or otherwise transfer your personal
                information to third parties, except in the following cases:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li>
                  <strong>Service Providers:</strong> We may share your data
                  with trusted third-party service providers who assist us in
                  operating our website, processing payments, or providing
                  customer service. These providers are obligated to maintain
                  the confidentiality and security of your information.
                </li>
                <li>
                  <strong>Legal Compliance:</strong> We may disclose your
                  information to comply with legal obligations, enforce our site
                  policies, or protect our rights or the rights of others.
                </li>
              </ul>
            </div>

            {/* Your Rights and Choices */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Your Rights and Choices
              </h2>
              <p className="text-gray-700 mb-4">
                You have the following rights regarding your personal
                information:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li>
                  <strong>Access and Correction:</strong> You may request access
                  to your personal information and request corrections or
                  updates to your data.
                </li>
                <li>
                  <strong>Opt-Out:</strong> If you no longer wish to receive
                  marketing communications from us, you can opt-out by clicking
                  the unsubscribe link in any email or by contacting us
                  directly.
                </li>
                <li>
                  <strong>Deletion:</strong> You may request that we delete your
                  personal information, subject to applicable legal and
                  regulatory obligations.
                </li>
              </ul>
            </div>

            {/* Cookies */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Cookie className="w-6 h-6 text-orange-600" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Cookies and Tracking Technologies
                </h2>
              </div>
              <p className="text-gray-700">
                We use cookies and similar technologies to enhance your
                experience on our website. You can choose to disable cookies
                through your browser settings, but this may affect your ability
                to access certain features of our website. For more information,
                please refer to our Cookie Policy.
              </p>
            </div>

            {/* Data Retention */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Data Retention
              </h2>
              <p className="text-gray-700">
                We retain your personal information for as long as necessary to
                fulfill the purposes for which it was collected, including
                legal, accounting, or reporting requirements.
              </p>
            </div>

            {/* Third-Party Links */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Third-Party Links
              </h2>
              <p className="text-gray-700">
                Our website may contain links to third-party websites. We are
                not responsible for the privacy practices of these external
                sites, and we encourage you to review their privacy policies
                before providing any personal information.
              </p>
            </div>

            {/* Children's Privacy */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Children's Privacy
              </h2>
              <p className="text-gray-700">
                Our website is not intended for children under the age of 13. We
                do not knowingly collect or solicit personal information from
                children. If we learn that we have inadvertently collected such
                information, we will delete it as soon as possible.
              </p>
            </div>

            {/* Changes to Privacy Policy */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700">
                We reserve the right to modify or update this Privacy Policy at
                any time. Any changes will be posted on this page with an
                updated "Effective Date." We encourage you to review this policy
                periodically to stay informed about how we are protecting your
                information.
              </p>
            </div>

            {/* Refund Poilcy */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Refund Policy
              </h2>
              <p className="text-gray-700">
                By purchasing any of our trademark search, clearance, filing, or
                related legal support services, you acknowledge and agree to the
                following refund policy:
              </p>
              <p className="text-gray-700">
                <b>1. Service Fees</b>
                <br />
                Our service fees cover professional time, attorney review,
                administrative processing, legal research, and preparation of
                your trademark matter. These fees are earned as services are
                performed.
              </p>
              <p className="text-gray-700">
                <b>2. Refund Requests Before Work Begins</b>
                <br />
                If you request to cancel your order immediately after purchase
                and **before any substantive work has commenced**, including but
                not limited to attorney assignment, trademark search, legal
                review, or preparation of your clearance report, you may be
                eligible for a **partial refund**. Any refund issued will be
                subject to deduction of applicable payment processing fees,
                administrative costs, and any work already performed.
              </p>
              <p className="text-gray-700">
                <b>3. Non-Refundable After Clearance Report</b>
                <br />
                Once your attorney-conducted trademark search and clearance
                report has been completed and delivered to you, the **service
                fee becomes fully earned and is non-refundable**, regardless of
                whether you decide to proceed with filing, respond to our
                communications, or discontinue the process.
              </p>
            </div>

            {/* Changes to Privacy Policy */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700">
                We reserve the right to modify or update this Privacy Policy at
                any time. Any changes will be posted on this page with an
                updated "Effective Date." We encourage you to review this policy
                periodically to stay informed about how we are protecting your
                information.
              </p>
            </div>

            {/* Contact Us */}
            <div className="bg-primary/10 p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Contact Us
                </h2>
              </div>
              <p className="text-gray-700 mb-4">
                If you have any questions or concerns about this Privacy Policy
                or how we handle your personal information, please contact us
                at:
              </p>
              <div className="space-y-2 text-gray-700">
                <p>
                  <strong>Company:</strong> Assess Trademark
                </p>
                <p>
                  <strong>Address:</strong> 515 Flower St, Los Angeles, CA 90071
                </p>
                <p>
                  <strong>Phone:</strong> +1 (844) 963-1925
                </p>
              </div>
              <p className="text-gray-600 mt-6 italic">
                By using our website and services, you consent to the terms of
                this Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
